// src/CameraMotionBlur.tsx
import { AbsoluteFill, Freeze, useCurrentFrame } from "remotion";
import { jsx } from "react/jsx-runtime";
var getNumberOfSamples = ({
  shutterFraction,
  samples,
  currentFrame
}) => {
  const maxOffset = shutterFraction * samples;
  if (maxOffset === 0) {
    return 1;
  }
  const maxTimeReverse = currentFrame - maxOffset;
  const factor = Math.min(1, Math.max(0, maxTimeReverse / maxOffset + 1));
  return Math.max(1, Math.round(Math.min(factor * samples, samples)));
};
var CameraMotionBlur = ({
  children,
  shutterAngle = 180,
  samples = 10
}) => {
  const currentFrame = useCurrentFrame();
  if (typeof samples !== "number" || Number.isNaN(samples) || !Number.isFinite(samples)) {
    throw new TypeError(`"samples" must be a number, but got ${JSON.stringify(samples)}`);
  }
  if (samples % 1 !== 0) {
    throw new TypeError(`"samples" must be an integer, but got ${JSON.stringify(samples)}`);
  }
  if (samples < 0) {
    throw new TypeError(`"samples" must be non-negative, but got ${JSON.stringify(samples)}`);
  }
  if (typeof shutterAngle !== "number" || Number.isNaN(shutterAngle) || !Number.isFinite(shutterAngle)) {
    throw new TypeError(`"shutterAngle" must be a number, but got ${JSON.stringify(shutterAngle)}`);
  }
  if (shutterAngle < 0 || shutterAngle > 360) {
    throw new TypeError(`"shutterAngle" must be between 0 and 360, but got ${JSON.stringify(shutterAngle)}`);
  }
  const shutterFraction = shutterAngle / 360;
  const actualSamples = getNumberOfSamples({
    currentFrame,
    samples,
    shutterFraction
  });
  return /* @__PURE__ */ jsx(AbsoluteFill, {
    style: { isolation: "isolate" },
    children: new Array(actualSamples).fill(true).map((_, i) => {
      const sample = i + 1;
      const sampleFrameOffset = shutterFraction * (sample / actualSamples);
      return /* @__PURE__ */ jsx(AbsoluteFill, {
        style: {
          mixBlendMode: "plus-lighter",
          filter: `opacity(${1 / actualSamples})`
        },
        children: /* @__PURE__ */ jsx(Freeze, {
          frame: currentFrame - sampleFrameOffset + 1,
          children
        })
      }, `frame-${i.toString()}`);
    })
  });
};
// src/HtmlInCanvasMotionBlur.tsx
import { useCallback } from "react";
import {
  Freeze as Freeze2,
  HtmlInCanvas,
  Interactive,
  Internals,
  useCurrentFrame as useCurrentFrame2,
  useVideoConfig
} from "remotion";
import { jsx as jsx2 } from "react/jsx-runtime";
var htmlInCanvasMotionBlurSchema = {
  ...Interactive.baseSchema,
  ...Interactive.premountSchema,
  shutterAngle: {
    type: "number",
    min: 0,
    max: 360,
    step: 1,
    default: 180,
    description: "Shutter angle",
    hiddenFromList: false
  },
  samples: {
    type: "number",
    min: 1,
    max: 64,
    step: 1,
    integer: true,
    default: 8,
    description: "Samples",
    hiddenFromList: false,
    keyframable: false
  },
  disabled: {
    type: "boolean",
    default: false,
    description: "Disabled"
  }
};
var MotionBlurSample = ({
  children,
  width,
  height,
  index,
  count,
  shutterInFrames,
  firstFrame,
  lastFrame
}) => {
  const frame = useCurrentFrame2();
  const sampleFrame = Math.max(firstFrame, Math.min(lastFrame, frame + ((index + 0.5) / count - 0.5) * shutterInFrames));
  const content = /* @__PURE__ */ jsx2(Freeze2, {
    frame: sampleFrame,
    children
  });
  const isRepresentativeSample = index === Math.floor(count / 2);
  return /* @__PURE__ */ jsx2("div", {
    "aria-hidden": !isRepresentativeSample,
    ...index === 0 ? {} : { drawable: "" },
    style: {
      position: "absolute",
      inset: 0,
      width,
      height,
      isolation: "isolate",
      visibility: "visible",
      pointerEvents: isRepresentativeSample ? "auto" : "none"
    },
    children: isRepresentativeSample ? content : /* @__PURE__ */ jsx2(Internals.DisableSequenceRegistrationProvider, {
      children: content
    })
  });
};
var HtmlInCanvasMotionBlurInner = ({
  children,
  width,
  height,
  shutterAngle = 180,
  samples = 8,
  disabled = false,
  from,
  durationInFrames,
  trimBefore,
  playbackRate,
  loop,
  freeze,
  hidden,
  name,
  premountFor,
  postmountFor,
  showInTimeline,
  styleWhilePremounted,
  styleWhilePostmounted,
  controls
}) => {
  const { durationInFrames: compositionDurationInFrames } = useVideoConfig();
  if (!Number.isInteger(samples) || samples < 1 || samples > 64) {
    throw new TypeError(`"samples" must be an integer between 1 and 64, but got ${String(samples)}`);
  }
  if (typeof shutterAngle !== "number" || !Number.isFinite(shutterAngle) || shutterAngle < 0 || shutterAngle > 360) {
    throw new TypeError(`"shutterAngle" must be between 0 and 360, but got ${String(shutterAngle)}`);
  }
  const actualSamples = disabled || shutterAngle === 0 ? 1 : samples;
  const shutterInFrames = shutterAngle / 360 * (playbackRate ?? 1);
  const firstFrame = trimBefore ?? 0;
  const visibleDuration = Math.max(0, Math.min(Internals.resolveSequenceDuration({
    durationInFrames,
    playbackRate,
    loop
  }), compositionDurationInFrames - (from ?? 0)));
  const lastFrame = firstFrame + Math.max(0, visibleDuration - 1) * (playbackRate ?? 1);
  const onPaint = useCallback(({ canvas, element, elementImage }) => {
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Failed to acquire 2D context for motion blur");
    }
    const layoutCanvas = element.parentElement;
    if (!(layoutCanvas instanceof HTMLCanvasElement)) {
      throw new Error("Failed to find the HTML-in-canvas layout canvas");
    }
    const captureElements = Array.from(layoutCanvas.children);
    if (captureElements.length !== actualSamples || captureElements[0] !== element) {
      throw new Error("Motion blur samples are not ready to paint");
    }
    context.reset();
    context.globalCompositeOperation = "lighter";
    context.globalAlpha = 1 / actualSamples;
    for (const [index, sampleElement] of captureElements.entries()) {
      const image = index === 0 ? elementImage : layoutCanvas.captureElementImage(sampleElement);
      try {
        context.drawElementImage(image, 0, 0);
      } finally {
        if (index !== 0) {
          image.close();
        }
      }
    }
  }, [actualSamples]);
  const passThrough = Internals.useHtmlInCanvasUnsupportedMessage() !== null;
  const sampleElements = Array.from({ length: passThrough ? 0 : actualSamples }, (_, index) => {
    return /* @__PURE__ */ jsx2(MotionBlurSample, {
      width,
      height,
      index,
      count: actualSamples,
      shutterInFrames,
      firstFrame,
      lastFrame,
      children
    }, index);
  });
  return /* @__PURE__ */ jsx2(HtmlInCanvas, {
    width,
    height,
    from,
    durationInFrames,
    trimBefore,
    playbackRate,
    loop,
    freeze,
    hidden,
    name: name ?? "<HtmlInCanvasMotionBlur>",
    premountFor,
    postmountFor,
    showInTimeline,
    styleWhilePremounted,
    styleWhilePostmounted,
    ...{ controls },
    onPaint,
    _remotionInternalCanvasSiblings: sampleElements.slice(1),
    _remotionInternalFallbackLabel: "Motion blur is not visible",
    children: passThrough ? children : sampleElements[0]
  });
};
var HtmlInCanvasMotionBlur = Interactive.withSchema({
  Component: HtmlInCanvasMotionBlurInner,
  componentName: "<HtmlInCanvasMotionBlur>",
  componentIdentity: "dev.remotion.motionBlur.HtmlInCanvasMotionBlur",
  schema: htmlInCanvasMotionBlurSchema,
  supportsEffects: false
});
HtmlInCanvasMotionBlur.displayName = "HtmlInCanvasMotionBlur";
// src/Trail.tsx
import { AbsoluteFill as AbsoluteFill2, Freeze as Freeze3, useCurrentFrame as useCurrentFrame3 } from "remotion";
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
var Trail = ({
  children,
  layers,
  lagInFrames,
  trailOpacity
}) => {
  const frame = useCurrentFrame3();
  if (typeof layers !== "number" || Number.isNaN(layers) || !Number.isFinite(layers)) {
    throw new TypeError(`"layers" must be a number, but got ${JSON.stringify(layers)}`);
  }
  if (layers % 1 !== 0) {
    throw new TypeError(`"layers" must be an integer, but got ${JSON.stringify(layers)}`);
  }
  if (layers < 0) {
    throw new TypeError(`"layers" must be non-negative, but got ${JSON.stringify(layers)}`);
  }
  if (typeof trailOpacity !== "number" || Number.isNaN(trailOpacity) || !Number.isFinite(trailOpacity)) {
    throw new TypeError(`"trailOpacity" must be a number, but got ${JSON.stringify(trailOpacity)}`);
  }
  if (typeof lagInFrames !== "number" || Number.isNaN(lagInFrames) || !Number.isFinite(lagInFrames)) {
    throw new TypeError(`"lagInFrames" must be a number, but got ${JSON.stringify(lagInFrames)}`);
  }
  return /* @__PURE__ */ jsxs(AbsoluteFill2, {
    children: [
      new Array(layers).fill(true).map((_, i) => {
        return /* @__PURE__ */ jsx3(AbsoluteFill2, {
          style: {
            opacity: trailOpacity - (layers - i) / layers * trailOpacity
          },
          children: /* @__PURE__ */ jsx3(Freeze3, {
            frame: frame - lagInFrames * (layers - i),
            children
          })
        }, `frame-${i.toString()}`);
      }),
      children
    ]
  });
};
export {
  CameraMotionBlur,
  HtmlInCanvasMotionBlur,
  Trail
};
